import { useCallback, useEffect, useRef, useState } from "react";

const CLOSE_DELAY_MS = 150;

/** Where a rail flyout stands and when it goes: beside a row at once, a beat
 * after the pointer leaves the row, at once when it leaves the flyout. */
export function useSidebarFlyout<K>() {
  const [flyout, setFlyout] = useState<{ key: K; top: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  // Fixed from the row's rect at open time, so anything that moves the row
  // strands it; scroll is captured, as it does not bubble from the rail's nav.
  useEffect(() => {
    if (!flyout) return;
    const close = () => setFlyout(null);
    // Scrolling the flyout's own list moves nothing it is anchored to.
    const closeOnScroll = (e: Event) => {
      if (e.target instanceof Element && e.target.closest(".sidebar-dropdown"))
        return;
      close();
    };
    window.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [flyout]);

  const show = useCallback((key: K, row: Element) => {
    clearTimeout(timer.current);
    setFlyout({ key, top: row.getBoundingClientRect().top });
  }, []);
  const hideSoon = useCallback(() => {
    timer.current = setTimeout(() => setFlyout(null), CLOSE_DELAY_MS);
  }, []);
  const hide = useCallback(() => setFlyout(null), []);
  const onHoverChange = useCallback((hovered: boolean) => {
    if (hovered) clearTimeout(timer.current);
    else setFlyout(null);
  }, []);
  return { flyout, show, hideSoon, hide, onHoverChange };
}
